/**
 * The exposure-control `transferId` (specification section 3.3), computed in the browser so a
 * source receipt can be matched to the transfer this page committed.
 *
 * `transferId` commits to the route, the source sequence, the sender, the amount, the asset
 * decimals, the source policy version, the recipient and the commitment time. The contract
 * emits it in `TransferCommitted` with the other fields; recomputing it from those fields plus
 * the sender, recipient and decimals this page signed for proves the event is that transfer.
 * Pinned against core's `fixtures/exposure-id-vectors.json` (src/test/exposureTransferId.test.ts).
 */
import {
  encodeAbiParameters,
  keccak256,
  pad,
  stringToHex,
  toHex,
  type Hex,
} from "viem";

import { ADDRESS_KIND, evmAddressWord } from "./routeIdentity";

export const TRANSFER_TAG = keccak256(stringToHex("ASTRALBEAM_TRANSFER_V1"));
export const PROTOCOL_VERSION = 1n;

const MAX_U64 = (1n << 64n) - 1n;
const MAX_U256 = (1n << 256n) - 1n;
const WORD = /^0x[0-9a-f]{64}$/;

export interface TransferFields {
  routeId: Hex;
  sourceSequence: bigint;
  senderKind: number;
  senderValue: Hex;
  amount: bigint;
  decimals: number;
  sourcePolicyVersion: bigint;
  recipientKind: number;
  recipientValue: Hex;
  sourceCommitmentTimeMs: bigint;
}

function word(value: Hex): Hex {
  const lower = value.toLowerCase() as Hex;
  if (!WORD.test(lower)) throw new Error(`not a 32-byte word: ${value}`);
  return lower;
}

function inRange(name: string, v: bigint, max: bigint): bigint {
  if (v < 0n || v > max) throw new Error(`${name} out of range: ${v}`);
  return v;
}

/** `ExposureIds.transferId` / `exposure_ids::transfer_id`. */
export function transferId(t: TransferFields): Hex {
  if (!Number.isInteger(t.decimals) || t.decimals < 0 || t.decimals > 255)
    throw new Error(`decimals out of range: ${t.decimals}`);
  return keccak256(
    encodeAbiParameters(
      [
        { type: "bytes32" },
        { type: "uint256" },
        { type: "bytes32" },
        { type: "bytes32" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint256" },
        { type: "uint8" },
        { type: "uint256" },
        { type: "uint8" },
        { type: "bytes32" },
        { type: "uint64" },
      ],
      [
        TRANSFER_TAG,
        PROTOCOL_VERSION,
        word(t.routeId),
        pad(toHex(inRange("sourceSequence", t.sourceSequence, MAX_U64)), { size: 32 }),
        t.senderKind,
        word(t.senderValue),
        inRange("amount", t.amount, MAX_U256),
        t.decimals,
        inRange("sourcePolicyVersion", t.sourcePolicyVersion, MAX_U256),
        t.recipientKind,
        word(t.recipientValue),
        inRange("sourceCommitmentTimeMs", t.sourceCommitmentTimeMs, MAX_U64),
      ],
    ),
  );
}

export interface AddressWord {
  kind: number;
  value: Hex;
}

/** An EVM account as a transfer records it (the sender of an EVM commitment). */
export const evmParty = (address: string): AddressWord => ({
  kind: ADDRESS_KIND.EVM,
  value: evmAddressWord(address),
});
